import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Stack, Switch, Typography } from '@mui/material';
import DialogModal from 'dashboard/layouts/auth/types/popup';
import { glassBar, useGlass } from 'styles/glass';
import { toast } from 'react-toastify';
import EntryFormField from './EntryFormField';
import { describeField, HIDDEN_FIELDS } from './entryFields';

/**
 * Add, edit or delete one entry in the content library.
 *
 * The form is built from the content type's own field list, which the API
 * sends as plain names. What each name means — prose, a picture, a date, a
 * yes/no — is worked out in `entryFields`, so this file is about layout and
 * saving rather than about guessing at types.
 *
 * Short fields pair up two to a row on a wide screen; prose and pictures take
 * the full width. The single narrow column this replaced wasted two thirds of
 * the dialog and made a blog post look like a contact form.
 *
 * Whether the entry is published lives beside the save button rather than in
 * the field list. It is not a property of the writing, it is the decision
 * about whether anyone sees it — the last thing you settle, next to the
 * button that settles it.
 */
export default function ActionPopup(props) {
  const GLASS = useGlass();
  const {
    name, popupModal, openDialog, setOpenDialog, entryData, selectedBrand,
    handleCreate, handleDelete, handleEdit,
  } = props;

  const [formData, setFormData] = useState(entryData?.data || {});
  const [mode, setMode] = useState(entryData?.mode || 'add');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFormData(entryData?.data?._id ? entryData.data : {});
    setMode(entryData?.mode || 'add');
  }, [entryData]);

  // The field list comes from the API. When that call fails there is none,
  // and mapping over it threw while rendering — so the Add button appeared to
  // do nothing at all, with the real error only in the console.
  const fields = useMemo(() => (Array.isArray(popupModal) ? popupModal : [])
    .filter((f) => typeof f === 'string' && !HIDDEN_FIELDS.has(f.toLowerCase()))
    .map(describeField), [popupModal]);

  const hasPublished = useMemo(
    () => (Array.isArray(popupModal) ? popupModal : []).includes('published'),
    [popupModal],
  );

  const setField = (fieldName) => (e) => {
    const target = e?.target || {};
    // Read by control type. `value ? value : checked` made an emptied text
    // field unsaveable, because the fallback for a text input is undefined.
    const next = target.type === 'checkbox' ? target.checked : target.value;
    setFormData((current) => ({ ...current, [fieldName]: next }));
  };

  const close = () => setOpenDialog(false);

  const submit = () => {
    const action = mode === 'delete' ? handleDelete
      : mode === 'edit' ? handleEdit
        : handleCreate;

    if (typeof action !== 'function') {
      // Guarded because this component is generic and the handlers come from
      // whoever mounted the table. They were once not forwarded at all, and
      // the failure was an uncaught TypeError on click.
      toast.error('This screen cannot save yet — nothing was sent.');
      return;
    }

    setSaving(true);
    // Images are uploaded as they are chosen and stored as addresses, so
    // there is no file to carry here. The second argument stays for callers
    // that still expect the old multipart shape.
    Promise.resolve(action(formData, null))
      .then(() => close())
      .catch((err) => toast.error(err?.message || 'Could not save that'))
      .finally(() => setSaving(false));
  };

  const title = mode === 'add' ? `Add ${name}`
    : mode === 'edit' ? `Edit ${name}`
      : `Delete ${name}`;

  return (
    <DialogModal
      title={title}
      isOpenAction={openDialog}
      onCloseAction={close}
      // The save button is pinned below a scrolling field list, so this needs
      // the full dialog height rather than one scrolling block — on a type
      // with many fields the button used to scroll out of reach.
      layout='flex'
      contentPadding={0}
      key={name}
    >
      <Stack direction='column' sx={{ width: '100%', height: '100%', minHeight: 0 }}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            width: '100%',
            px: { xs: '16px', md: '24px' },
            pt: '20px',
            pb: '20px',
          }}
        >
          {mode === 'delete' ? (
            <Typography variant='body1'>
              Delete “{formData.title || formData.name || formData.headline || `this ${name}`}”?
              This cannot be undone.
            </Typography>
          ) : fields.length === 0 ? (
            <Alert severity='warning'>
              We could not load the fields for {name}. Reload the page and try
              again — if it keeps happening, that content type could not be read.
            </Alert>
          ) : (
            <Box
              sx={{
                display: 'grid',
                // Two columns of short fields on a wide dialog, one on a
                // phone. Prose and pictures span both.
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                columnGap: '18px',
                rowGap: '18px',
                alignItems: 'start',
              }}
            >
              {fields.map((field) => (
                <Box
                  key={field.name}
                  sx={{ gridColumn: field.fullWidth ? { md: '1 / -1' } : 'auto', minWidth: 0 }}
                >
                  <EntryFormField
                    field={field}
                    value={formData[field.name]}
                    brandId={selectedBrand}
                    onChange={setField(field.name)}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          flexWrap='wrap'
          gap='12px'
          sx={{
            flexShrink: 0,
            width: '100%',
            px: { xs: '16px', md: '24px' },
            py: '14px',
            borderTop: GLASS.divider,
            // Frosted so the fields scrolling under it stay behind it.
            ...glassBar(GLASS),
          }}
        >
          {mode !== 'delete' && hasPublished ? (
            <Stack direction='row' alignItems='center' spacing='6px'>
              <Switch
                size='small'
                checked={Boolean(formData.published)}
                onChange={setField('published')}
                inputProps={{ 'aria-label': 'Published' }}
              />
              <Box>
                <Typography variant='body2' fontWeight='600' lineHeight={1.2}>
                  {formData.published ? 'Live on your site' : 'Not published'}
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {formData.published
                    ? 'Visitors can see this.'
                    : 'Saved, but nobody else sees it yet.'}
                </Typography>
              </Box>
            </Stack>
          ) : <Box />}

          <Stack direction='row' spacing='8px'>
            <Button onClick={close} disabled={saving}>Cancel</Button>
            <Button
              variant='contained'
              color={mode === 'delete' ? 'error' : 'primary'}
              onClick={submit}
              disabled={saving || (mode !== 'delete' && fields.length === 0)}
              sx={{ borderRadius: '14px', fontWeight: 600, minWidth: 130, height: 44 }}
            >
              {saving ? 'Saving…' : mode === 'delete' ? 'Delete' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </DialogModal>
  );
}
